import { useEffect, useState } from "react";
import { BadgeCheck, Check, Home, Layers, PlusCircle, ScanLine, ShoppingBag, ArrowLeft } from "lucide-react";
import { IMG } from "@/data/vomarcData";
import { CartScreen, DesignScreen, ElectricalScreen, HomeScreen, NotificationsScreen, PassportScreen, ScanScreen, TrackScreen, money } from "./PhoneScreens";

const WALLS = [
  { id: "warm", label: "Warm White", v: "#efe7d8" },
  { id: "sage", label: "Sage", v: "#8ba888" },
  { id: "clay", label: "Clay", v: "#c98a6a" },
  { id: "slate", label: "Slate", v: "#5a6b74" },
  { id: "blush", label: "Blush", v: "#e3b7ab" },
  { id: "ink", label: "Ink", v: "#2f3540" },
];

const PRODUCTS = [
  { id: "sofa", name: "Modular Sofa", category: "Furniture", price: 42000, img: IMG.arRoom, fit: "fits" },
  { id: "lamp", name: "Arc Floor Lamp", category: "Lighting", price: 8500, img: IMG.designer, fit: "fits" },
  { id: "paint", name: "Sage Emulsion 10L", category: "Paint", price: 3200, img: IMG.market, fit: "fits" },
  { id: "shelf", name: "Oak Wall Shelf", category: "Decor", price: 6900, img: IMG.warranty, fit: "blocks" },
  { id: "table", name: "Teak Coffee Table", category: "Furniture", price: 15000, img: IMG.scrap, fit: "fits" },
  { id: "pendant", name: "Pendant Light", category: "Lighting", price: 4200, img: IMG.vendor, fit: "fits" },
];

const CATEGORIES = ["All", "Furniture", "Lighting", "Paint", "Decor"];

const MILESTONES = [
  { id: "prep", label: "Surface prep", img: IMG.workforce, gps: "09:12 AM", tranche: 45 },
  { id: "primer", label: "Primer", img: IMG.scan, gps: "11:40 AM", tranche: 30 },
  { id: "coat1", label: "Coat 1", img: IMG.arRoom, gps: "02:05 PM", tranche: 25 },
  { id: "final", label: "Final", img: IMG.market, gps: "pending", tranche: 0 },
];

const BIDS = [
  { id: "b1", name: "Vendor A", price: "₹1.87L", days: "6 days", rating: "4.8" },
  { id: "b2", name: "Vendor B", price: "₹2.10L", days: "5 days", rating: "4.6" },
  { id: "b3", name: "Vendor C", price: "₹1.96L", days: "7 days", rating: "4.7" },
];

const WORKERS_INIT = [
  { id: "w1", name: "Suresh", img: IMG.workforce, in: true },
  { id: "w2", name: "Anil", img: IMG.vendor, in: false },
  { id: "w3", name: "Manu", img: IMG.designer, in: false },
];

const TABS = [
  { id: "home", label: "Home", icon: Home },
  { id: "scan", label: "Scan", icon: ScanLine },
  { id: "design", label: "Design", icon: Layers },
  { id: "shop", label: "Shop", icon: ShoppingBag },
  { id: "track", label: "Track", icon: BadgeCheck },
];

export function PhonePrototype() {
  const [screen, setScreen] = useState("home");
  const [flash, setFlash] = useState("");
  const [notifications, setNotifications] = useState([{ id: "n1", text: "Primer coat scheduled for tomorrow 10 AM" }, { id: "n2", text: "Vendor A sent a revised bid" }]);
  // scan
  const [scanState, setScanState] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [layer, setLayer] = useState("wiring");
  // design
  const [wall, setWall] = useState(WALLS[0]);
  const [product, setProduct] = useState(PRODUCTS[0]);
  const [fitChecked, setFitChecked] = useState(false);
  const [lighting, setLighting] = useState("day");
  const [before, setBefore] = useState(false);
  const [looks, setLooks] = useState(0);
  // shop
  const [category, setCategory] = useState("All");
  const [cart, setCart] = useState([]);
  const [shopView, setShopView] = useState("list");
  const [mode, setMode] = useState("deliver");
  const [bids, setBids] = useState([]);
  const [acceptedBid, setAcceptedBid] = useState(null);
  // track
  const [verified, setVerified] = useState([]);
  const [released, setReleased] = useState(0);
  const [workers, setWorkers] = useState(WORKERS_INIT);
  const [qc, setQc] = useState("idle");

  const toast = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 1800); };
  const notify = (text) => setNotifications((n) => [{ id: `${Date.now()}-${n.length}`, text }, ...n]);

  useEffect(() => {
    if (scanState !== "scanning") return undefined;
    const t = setInterval(() => setProgress((p) => {
      if (p >= 100) { clearInterval(t); setScanState("done"); notify("Room scan complete · 15.6 m²"); return 100; }
      return p + 4;
    }), 110);
    return () => clearInterval(t);
  }, [scanState]);

  const startScan = () => { setProgress(0); setScanState("scanning"); };
  const resetScan = () => { setScanState("idle"); setProgress(0); };

  const filteredProducts = category === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === category);
  const addToCart = (p) => {
    setCart((c) => (c.find((x) => x.id === p.id) ? c.map((x) => (x.id === p.id ? { ...x, qty: x.qty + 1 } : x)) : [...c, { ...p, qty: 1 }]));
    toast(`${p.name} added to cart`);
  };
  const inc = (id) => setCart((c) => c.map((x) => (x.id === id ? { ...x, qty: x.qty + 1 } : x)));
  const dec = (id) => setCart((c) => c.map((x) => (x.id === id ? { ...x, qty: Math.max(1, x.qty - 1) } : x)));
  const remove = (id) => setCart((c) => c.filter((x) => x.id !== id));
  const total = cart.reduce((n, c) => n + c.price * c.qty, 0);
  const checkout = () => { toast(`Order placed · ${money(total)} (Demo)`); notify(`Order confirmed · ${money(total)}`); setCart([]); setShopView("list"); };

  const getBids = () => {
    setShopView("bids"); setBids([]); setAcceptedBid(null);
    BIDS.forEach((b, i) => setTimeout(() => setBids((cur) => [...cur, b]), (i + 1) * 700));
  };
  const acceptBid = (b) => { setAcceptedBid(b.id); toast(`${b.name} accepted`); notify(`${b.name} accepted · ${b.price}`); };

  const verifyMilestone = (id) => { setVerified((v) => (v.includes(id) ? v : [...v, id])); toast("Milestone verified on site"); };
  const releasePayment = () => { const tranche = MILESTONES[released]?.tranche || 0; setReleased((r) => r + 1); toast(`Released ${tranche}% payment`); notify(`Payment released · ${tranche}%`); };
  const canRelease = verified.length > released && released < 3;
  const checkIn = (id) => { setWorkers((w) => w.map((x) => (x.id === id ? { ...x, in: !x.in } : x))); toast("GPS + selfie check-in recorded"); };
  const runQc = () => { setQc("running"); setTimeout(() => { setQc("done"); toast("AI QC passed · 98% match"); }, 1600); };

  const saveLook = () => { setLooks((l) => l + 1); toast(`Look saved · ${wall.label} + ${product.name}`); };
  const goTab = (id) => { setScreen(id); if (id === "shop") setShopView("list"); };

  return (
    <div className="pp-wrap" data-testid="phone-prototype">
      <span className="pp-try-label" data-testid="pp-try-label">Try the app · every button works</span>
      <div className="pp-phone" data-testid="pp-phone-frame">
        <div className="pp-notch" />
        <span className="pp-demo-chip" data-testid="pp-demo-chip">Demo</span>
        <div className="pp-screen" data-testid="pp-screen">
          {screen === "home" && <HomeScreen go={goTab} notifications={notifications.length} onBell={() => setScreen("notifications")} cartCount={cart.reduce((n, c) => n + c.qty, 0)} />}
          {screen === "notifications" && <NotificationsScreen back={() => setScreen("home")} items={notifications} onClear={() => setNotifications([])} />}
          {screen === "passport" && <PassportScreen back={() => setScreen("home")} toast={toast} />}
          {screen === "scan" && <ScanScreen state={scanState} progress={progress} start={startScan} reset={resetScan} go={goTab} />}
          {screen === "electrical" && <ElectricalScreen back={() => setScreen("scan")} layer={layer} setLayer={setLayer} toast={toast} />}
          {screen === "design" && (
            <DesignScreen wall={wall} setWall={setWall} walls={WALLS} product={product} setProduct={setProduct} products={PRODUCTS} fitChecked={fitChecked} setFitChecked={setFitChecked} lighting={lighting} setLighting={setLighting} before={before} setBefore={setBefore} saveLook={saveLook} looks={looks} />
          )}

          {screen === "shop" && shopView === "list" && (
            <div className="pp-view" data-testid="pp-shop">
              <h4 className="pp-h">Marketplace</h4>
              <div className="pp-chips" data-testid="pp-shop-categories">
                {CATEGORIES.map((c) => <button key={c} className={`pp-cat ${category === c ? "on" : ""}`} onClick={() => setCategory(c)} data-testid={`pp-cat-${c}`}>{c}</button>)}
              </div>
              <div className="pp-shop-list" data-testid="pp-shop-list">
                {filteredProducts.map((p) => (
                  <div key={p.id} className="pp-shop-card" data-testid={`pp-shop-item-${p.id}`}>
                    <img src={p.img} alt={p.name} />
                    <div><strong>{p.name}</strong><span>{money(p.price)} · {p.fit === "fits" ? "Fits your room" : "Check fit"}</span></div>
                    <button className="pp-add" onClick={() => addToCart(p)} data-testid={`pp-add-${p.id}`}><PlusCircle size={18} /></button>
                  </div>
                ))}
              </div>
              <button className="pp-btn primary" onClick={() => setShopView("cart")} data-testid="pp-view-cart">View cart ({cart.reduce((n, c) => n + c.qty, 0)})</button>
            </div>
          )}
          {screen === "shop" && shopView === "cart" && (
            <CartScreen back={() => setShopView("list")} cart={cart} inc={inc} dec={dec} remove={remove} total={total} mode={mode} setMode={setMode} checkout={checkout} getBids={getBids} />
          )}
          {screen === "shop" && shopView === "bids" && (
            <div className="pp-view" data-testid="pp-bids">
              <button className="pp-back" onClick={() => setShopView("cart")} data-testid="pp-bids-back"><ArrowLeft size={14} /> Cart</button>
              <h4 className="pp-h">Vendor bids</h4>
              <div className="pp-bid-list" data-testid="pp-bid-list">
                {bids.map((b) => (
                  <button key={b.id} className={`pp-bid ${acceptedBid === b.id ? "accepted" : ""}`} onClick={() => acceptBid(b)} data-testid={`pp-bid-${b.id}`}>
                    <strong>{b.name}</strong><span>{b.price} · {b.days} · ★ {b.rating}</span>
                    {acceptedBid === b.id && <em><Check size={13} /> Accepted</em>}
                  </button>
                ))}
                {bids.length < 3 && <p className="pp-note" data-testid="pp-bids-loading">Collecting bids…</p>}
              </div>
              {acceptedBid && <button className="pp-btn primary" onClick={() => { goTab("track"); toast("Project started"); }} data-testid="pp-start-project">Start project</button>}
            </div>
          )}

          {screen === "track" && (
            <TrackScreen milestones={MILESTONES} verified={verified} verify={verifyMilestone} released={released} canRelease={canRelease} release={releasePayment} workers={workers} checkIn={checkIn} qc={qc} runQc={runQc} />
          )}
        </div>

        {flash && <div className="pp-flash" data-testid="pp-flash">{flash}</div>}

        <div className="pp-tabs" data-testid="pp-tabbar">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = screen === t.id || (t.id === "scan" && screen === "electrical") || (t.id === "home" && ["notifications", "passport"].includes(screen));
            return (
              <button key={t.id} className={active ? "on" : ""} onClick={() => goTab(t.id)} data-testid={`pp-tab-${t.id}`}>
                <Icon size={18} /><em>{t.label}</em>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
